import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import cx from "classnames"
import formatDistanceToNow from "date-fns/formatDistanceToNow"

import RoundAvatar from "../RoundAvatar"
import Button from "../Button"
import SignOffView from "./SignOffView"
import { SignOffStatus } from "./SignOffs"
import { getOrgSignOffs } from "../../services/api/v2/signOff"
import apiSignOff from "../../services/signOff/signOff.api"
import { getObjectId, getRefId } from "../../utils/utils"
import { SIGN_OFF_STATUS, SIGN_OFF_TYPE_LABEL, signOffLine } from "../../utils/signOffs"
import Badge from "../Badge"
import SimpleBarReact from "simplebar-react"
import "simplebar/dist/simplebar.min.css"
import { countWaitingSignOffs } from "./useWaitingSignOffs"
import "./SignOffs.scss"

const PAGE = 30

const TABS = [
    { id: "mine", label: "Waiting for me", params: { approver: "me", status: ["ready"] } },
    { id: "asked", label: "Asked by me", params: { requester: "me", open: true } },
    { id: "done", label: "Done", params: { open: false } },
]

export { countWaitingSignOffs }

/**
 * The person's sign-offs across their organisations: what waits for them,
 * what they asked for, what is done. A row opens the sign-off on its own
 * (SignOffView), which needs no place on the project.
 *
 * @param [onCountChange] {function(Number)} after a decision, the new "waiting for me" count
 */
const SignOffInbox = ({ app, onClose, onCountChange }) => {
    const [tab, setTab] = useState(TABS[0].id)
    const [rows, setRows] = useState(null)
    const [pages, setPages] = useState([])
    const [loading, setLoading] = useState(false)
    const [openId, setOpenId] = useState(null)
    const [counts, setCounts] = useState({ mine: 0, asked: 0 })
    const orgs = useMemo(() => app.state.orgs || [], [app.state.orgs])
    // Follows app.state.orgs: the people (and their avatars) arrive after the first render
    const orgById = useMemo(() => new Map(orgs.map((org) => [getObjectId(org._id), org])), [orgs])
    const mounted = useRef(true)

    // The app's navigator sits above this card; hide it while the inbox is up
    useEffect(() => {
        app.hideKnobs()

        return () => app.showKnobs()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const loadCounts = useCallback(async () => {
        const countTab = async (id) => {
            const { params } = TABS.find((t) => t.id === id)
            const all = await Promise.all(
                orgs.map((org) =>
                    getOrgSignOffs(getObjectId(org._id), { ...params, count: true, limit: 1 })
                        .then((res) => res?.count || 0)
                        .catch(() => 0),
                ),
            )

            return all.reduce((sum, n) => sum + n, 0)
        }
        const [mine, asked] = await Promise.all([countTab("mine"), countTab("asked")])

        if (mounted.current) setCounts({ mine, asked })
    }, [orgs])

    const load = useCallback(async () => {
        const current = TABS.find((t) => t.id === tab)

        loadCounts()
        setLoading(true)
        setRows(null)

        try {
            const responses = await Promise.all(
                orgs.map((org) =>
                    getOrgSignOffs(getObjectId(org._id), { ...current.params, limit: PAGE }).catch(() => null),
                ),
            )

            if (!mounted.current) return

            setPages(responses.filter(Boolean))
            setRows(
                responses
                    .filter(Boolean)
                    .flatMap((res) => res.value || [])
                    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)),
            )
        } finally {
            if (mounted.current) setLoading(false)
        }
    }, [tab, orgs, loadCounts])

    useEffect(() => {
        mounted.current = true
        load()

        return () => {
            mounted.current = false
        }
    }, [load])

    // A change anywhere reloads the inbox, once per burst: every reload is a round
    // of requests per organisation.
    useEffect(() => {
        let timer = null
        const apply = () => {
            clearTimeout(timer)
            timer = setTimeout(load, 500)
        }
        const created = apiSignOff.onChanges("create", apply)
        const updated = apiSignOff.onChanges("update", apply)

        return () => {
            clearTimeout(timer)
            created.disconnect()
            updated.disconnect()
        }
    }, [load])

    const loadMore = async () => {
        if (loading) return

        setLoading(true)

        try {
            const next = await Promise.all(pages.filter((p) => p.hasNext()).map((p) => p.getNext()))

            if (!mounted.current) return

            setPages([...pages.filter((p) => !p.hasNext()), ...next])
            setRows((current) =>
                [...(current || []), ...next.flatMap((res) => res.value || [])].sort(
                    (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
                ),
            )
        } finally {
            if (mounted.current) setLoading(false)
        }
    }

    const onChanged = () => {
        load()

        if (onCountChange) {
            countWaitingSignOffs(orgs).then(onCountChange)
        }
    }

    const hasMore = pages.some((p) => p.hasNext())
    const openSignOff = openId ? (rows || []).find((row) => row._id === openId) : null
    const personFor = (ref, name, org) => {
        const id = getRefId(ref)

        if (id && id === app.state.person?._id) return app.state.person

        const found = [...(org?.people || []), ...(org?.archivedPeople || [])].find((p) => getRefId(p) === id)

        return found ? (found.ref && typeof found.ref === "object" ? found.ref : found) : { _id: id, firstName: name }
    }

    return (
        <div className="x-sign-off-inbox">
            <div className="x-sign-off-inbox-tabs">
                {TABS.map((t) => (
                    <div
                        key={t.id}
                        className={cx("dna-pill-box x-sign-off-inbox-tab", { "x-pill": tab === t.id })}
                        onClick={() => setTab(t.id)}
                    >
                        <Badge badgeContent={t.id === "done" ? 0 : counts[t.id]} color="primary">
                            {t.label}
                        </Badge>
                    </div>
                ))}
            </div>

            <SimpleBarReact className="x-sign-off-inbox-list">
            {rows === null && <p>Loading...</p>}

            {rows !== null && !rows.length && (
                <p>
                    {tab === "mine"
                        ? "Nothing is waiting for your sign-off."
                        : tab === "asked"
                          ? "You have no open sign-off requests."
                          : "No sign-offs are done yet."}
                </p>
            )}

            {(rows || []).map((row) => {
                const org = orgById.get(getObjectId(row.org))
                const other =
                    tab === "mine"
                        ? personFor(row.requestedBy?.ref, row.requestedBy?.name, org)
                        : personFor(row.approver?.ref, row.approver?.name, org)

                return (
                    <div key={row._id} className="x-sign-off-inbox-row" onClick={() => setOpenId(row._id)}>
                        <RoundAvatar person={other} noTip />
                        <div className="x-sign-off-inbox-text">
                            <div className="x-clamp-1">{signOffLine(row)}</div>
                            <div className="dna-light-text dna-smaller-text x-clamp-1">
                                {SIGN_OFF_TYPE_LABEL[row.type] || row.type}
                                {org?.title ? ` · ${org.title}` : ""}
                                {" · "}
                                {tab === "mine"
                                    ? `asked by ${row.requestedBy?.name || "somebody"}`
                                    : row.approver?.name
                                      ? `approver ${row.approver.name}`
                                      : "no approver yet"}
                                {" · "}
                                {formatDistanceToNow(new Date(row.updatedAt), { addSuffix: true })}
                            </div>
                            {row.note && row.status !== SIGN_OFF_STATUS.READY && (
                                <div className="dna-smaller-text x-clamp-1">{row.note}</div>
                            )}
                        </div>
                        <SignOffStatus signOff={row} />
                    </div>
                )
            })}

            {hasMore && (
                <Button className={cx("transparent x-sign-off-inbox-more", { disabled: loading })} onClick={loadMore}>
                    Load more
                </Button>
            )}
            </SimpleBarReact>

            <SignOffView
                app={app}
                orgId={openSignOff ? getObjectId(openSignOff.org) : null}
                signOffId={openId}
                open={!!openId}
                onClose={() => setOpenId(null)}
                onChanged={(fresh) => {
                    onChanged()

                    if (fresh?.status === SIGN_OFF_STATUS.CANCELLED) setOpenId(null)
                }}
            />
        </div>
    )
}

export default SignOffInbox
